import { ThunkAction, ThunkDispatch } from "redux-thunk";
import * as tokenService from "../services/tokenService";
import * as calendarTaskService from "../services/calendarTaskService";
import * as frontService from "../services/frontService";
import * as messageService from "../services/messageService";
import * as eventService from "../services/eventService";
import * as taskService from "../services/taskService";
import * as documentService from "../services/documentService";
import * as userFurtherEducationService from "../services/userFurtherEducationService";
import * as recordUserService from "../services/recordUserService";
import * as instantMessageChannelService from "../services/instantMessageChannelService";
import * as storeTypes from "./storeTypes";
import * as storeActions from "./storeActions";
import { customHistory } from "../../framework/customHistory";
import { appIsUpdated, isInstanceOfIApiError } from "../services/baseService";
import { AppUtils } from "../../framework/appUtils";
import { DatabaseRecordType } from "ui/Scripts/source/models/enums";
import { Base } from "ui/Scripts/source/framework/base";

function forwardToLoginOnAuthenticationError(error: any) {
    if (error && "status" in error && error.status === 401) {
        customHistory.push("/login");
    }
}

function handleApiError(error: any, dispatch: ThunkDispatch<storeTypes.IApplicationState, any, any>) {
    console.log(error);
    if (isInstanceOfIApiError(error)) {
        forwardToLoginOnAuthenticationError(error);
        if (appIsUpdated(error.message)) {
            return;
        }
        dispatch(storeActions.showErrorMessage(error.message));
    } else {
        if (appIsUpdated(error)) {
            return;
        }
        dispatch(storeActions.showErrorMessage(error));
    }
}

function callServiceFromEffect<T>(serviceCall: () => Promise<T>, onBefore: () => void = null, onSuccess: (a: T) => void = null, onError: (e) => void = null, onFinally: () => void = null): Promise<void> {
    onBefore?.();
    return AppUtils.callService(serviceCall, false, false, onFinally, onError)
        .then(result => {
            if (!result) return;
            onSuccess?.(result);
        });
}

//Token
export type LogonThunkDispatch = ThunkDispatch<storeTypes.IApplicationState, null, storeTypes.LoginAction>

export const login = (username: string, password: string): ThunkAction<any, storeTypes.IApplicationState, any, any> => (dispatch, getState) => {
    dispatch(storeActions.fetchStart());
    return tokenService.login(username, password)
        .then(
            loginResult => {
                dispatch(storeActions.loginSuccess(loginResult));
                customHistory.push("/");
            },
            error => {
                handleApiError(error, dispatch);
                dispatch(storeActions.loginFailure());
            })
        .finally(() => dispatch(storeActions.fetchEnd()));
};

export const logout = (): ThunkAction<any, storeTypes.IApplicationState, any, storeTypes.LoginAction> => (dispatch, getState) => {
    tokenService.logout()
        .then(() => {
            dispatch(storeActions.logoutSuccess());
            customHistory.push("/login");
        });
};

//Front
export type FrontThunkDispatch = ThunkDispatch<storeTypes.IApplicationState, null, storeTypes.FrontAction>

export const getFrontPage = (): ThunkAction<any, storeTypes.IApplicationState, any, any> => (dispatch, getState) => {
    callServiceFromEffect(frontService.getFrontPage,
        () => dispatch(storeActions.frontPageRequest()),
        (result) => dispatch(storeActions.frontPageSuccess(result)),
        forwardToLoginOnAuthenticationError,
        () => dispatch(storeActions.frontPageFailure()));
};

export const getFrontPageMessage = (): ThunkAction<any, storeTypes.IApplicationState, any, any> => (dispatch, getState) => {
    callServiceFromEffect(frontService.getFrontPageMessage,
        () => dispatch(storeActions.frontPageMessageRequest()),
        (result) => dispatch(storeActions.frontPageMessageSuccess(result)),
        () => dispatch(storeActions.frontPageMessageFailure()));
};

export const getFrontPageEvent = (): ThunkAction<any, storeTypes.IApplicationState, any, any> => (dispatch, getState) => {
    callServiceFromEffect(frontService.getFrontPageEvent,
        () => dispatch(storeActions.frontPageEventRequest()),
        (result) => dispatch(storeActions.frontPageEventSuccess(result)),
        () => dispatch(storeActions.frontPageEventFailure()));
};

export const getFrontPageTask = (): ThunkAction<any, storeTypes.IApplicationState, any, any> => (dispatch, getState) => {
    callServiceFromEffect(frontService.getFrontPageTask,
        () => dispatch(storeActions.frontPageTaskRequest()),
        (result) => dispatch(storeActions.frontPageTaskSuccess(result)),
        () => dispatch(storeActions.frontPageTaskFailure()));
};

export const getFrontPageDocument = (): ThunkAction<any, storeTypes.IApplicationState, any, any> => (dispatch, getState) => {
    callServiceFromEffect(frontService.getFrontPageDocument,
        () => dispatch(storeActions.frontPageDocumentRequest()),
        (result) => dispatch(storeActions.frontPageDocumentSuccess(result)),
        () => dispatch(storeActions.frontPageDocumentFailure()));
};

export const getFrontPageInstantMessage = (): ThunkAction<any, storeTypes.IApplicationState, any, any> => (dispatch, getState) => {
    callServiceFromEffect(frontService.getFrontPageInstantMessage,
        () => dispatch(storeActions.frontPageInstantMessageRequest()),
        (result) => dispatch(storeActions.frontPageInstantMessageSuccess(result)),
        () => dispatch(storeActions.frontPageInstantMessageFailure()));
};

export const getFrontPageCalendarTask = (): ThunkAction<any, storeTypes.IApplicationState, any, any> => (dispatch, getState) => {
    callServiceFromEffect(frontService.getFrontPageCalendarTask,
        () => dispatch(storeActions.frontPageCalendarTaskRequest()),
        (result) => dispatch(storeActions.frontPageCalendarTaskSuccess(result)),
        () => dispatch(storeActions.frontPageCalendarTaskFailure()));
};

//Message
export type MessageThunkDispatch = ThunkDispatch<storeTypes.IApplicationState, null, storeTypes.MessageAction>

export const getMessageList = (page: number, startDate: number, endDate: number, companyId: string): ThunkAction<any, storeTypes.IApplicationState, any, any> => (dispatch, getState) => {
    dispatch(storeActions.messageListRequest());
    dispatch(storeActions.fetchStart());
    return messageService.getMessageList(page, startDate, endDate, companyId)
        .then(
            list => {
                dispatch(storeActions.messageListSuccess(list, startDate, endDate, companyId));
            },
            error => {
                handleApiError(error, dispatch);
                dispatch(storeActions.messageListFailure());
            })
        .finally(() => dispatch(storeActions.fetchEnd()));
};

export const messageListRefresh = (page: number, startDate: number, endDate: number, companyId: string): ThunkAction<any, storeTypes.IApplicationState, any, any> => (dispatch, getState) => {
    dispatch(storeActions.messageListRequest());
    dispatch(storeActions.fetchStart());
    return messageService.messageListRefresh(page, startDate, endDate, companyId)
        .then(
            list => {
                dispatch(storeActions.messageListRefresh(list, startDate, endDate, companyId));
            },
            error => {
                handleApiError(error, dispatch);
                dispatch(storeActions.messageListFailure());
            })
        .finally(() => dispatch(storeActions.fetchEnd()));
};

export const getMessageEdit = (id: string): ThunkAction<any, storeTypes.IApplicationState, any, any> => (dispatch, getState) => {
    dispatch(storeActions.messageEditRequest());
    dispatch(storeActions.fetchStart());
    return messageService.getMessageEdit(id)
        .then(
            edit => {
                dispatch(storeActions.messageEditSuccess(edit));
            },
            error => {
                handleApiError(error, dispatch);
                dispatch(storeActions.messageEditFailure());
                customHistory.push("/message");
            })
        .finally(() => dispatch(storeActions.fetchEnd()));
};

//Event
export type EventThunkDispatch = ThunkDispatch<storeTypes.IApplicationState, null, storeTypes.EventAction>

export const getEventList = (page: number, state: number, startDate: number, endDate: number, unitId: string): ThunkAction<any, storeTypes.IApplicationState, any, any> => (dispatch, getState) => {
    dispatch(storeActions.eventListRequest());
    dispatch(storeActions.fetchStart());
    return eventService.getEventList(page, state, startDate, endDate, unitId)
        .then(
            list => {
                dispatch(storeActions.eventListSuccess(list, state, startDate, endDate, unitId));
            },
            error => {
                handleApiError(error, dispatch);
                dispatch(storeActions.eventListFailure());
            })
        .finally(() => dispatch(storeActions.fetchEnd()));
};

export const eventListRefresh = (page: number, state: number, startDate: number, endDate: number, unitId: string): ThunkAction<any, storeTypes.IApplicationState, any, any> => (dispatch, getState) => {
    dispatch(storeActions.eventListRequest());
    dispatch(storeActions.fetchStart());
    return eventService.eventListRefresh(page, state, startDate, endDate, unitId)
        .then(
            list => {
                dispatch(storeActions.eventListRefresh(list, state, startDate, endDate, unitId));
            },
            error => {
                handleApiError(error, dispatch);
                dispatch(storeActions.eventListFailure());
            })
        .finally(() => dispatch(storeActions.fetchEnd()));
};

export const getEventEdit = (id: string): ThunkAction<any, storeTypes.IApplicationState, any, any> => (dispatch, getState) => {
    dispatch(storeActions.eventEditRequest());
    dispatch(storeActions.fetchStart());
    return eventService.getEventEdit(id)
        .then(
            edit => {
                dispatch(storeActions.eventEditSuccess(edit));
            },
            error => {
                handleApiError(error, dispatch);
                dispatch(storeActions.eventEditFailure());
                customHistory.push("/event");
            })
        .finally(() => dispatch(storeActions.fetchEnd()));
};

export const refreshEventLock = (id: string): ThunkAction<any, storeTypes.IApplicationState, any, any> => (dispatch, getState) => {
    dispatch(storeActions.fetchStart());
    return eventService.getLocked(id)
        .then(
            locked => {
                dispatch(storeActions.eventListSetLocked(id, locked));
            },
            error => {
                handleApiError(error, dispatch);
            })
        .finally(() => dispatch(storeActions.fetchEnd()));
};

//Task
export type TaskThunkDispatch = ThunkDispatch<storeTypes.IApplicationState, null, storeTypes.TaskAction>

export const getTaskList = (page: number, stateId: string, unitId: string): ThunkAction<any, storeTypes.IApplicationState, any, any> => (dispatch, getState) => {
    dispatch(storeActions.taskListRequest());
    dispatch(storeActions.fetchStart());
    return taskService.getTaskList(page, stateId, unitId)
        .then(
            list => {
                dispatch(storeActions.taskListSuccess(list, stateId, unitId));
            },
            error => {
                handleApiError(error, dispatch);
                dispatch(storeActions.taskListFailure());
            })
        .finally(() => dispatch(storeActions.fetchEnd()));
};

export const taskListRefresh = (page: number, stateId: string, unitId: string): ThunkAction<any, storeTypes.IApplicationState, any, any> => (dispatch, getState) => {
    dispatch(storeActions.taskListRequest());
    dispatch(storeActions.fetchStart());
    return taskService.taskListRefresh(page, stateId, unitId)
        .then(
            list => {
                dispatch(storeActions.taskListRefresh(list, stateId, unitId));
            },
            error => {
                handleApiError(error, dispatch);
                dispatch(storeActions.taskListFailure());
            })
        .finally(() => dispatch(storeActions.fetchEnd()));
};

export const getTaskEdit = (id: string): ThunkAction<any, storeTypes.IApplicationState, any, any> => (dispatch, getState) => {
    dispatch(storeActions.taskEditRequest());
    dispatch(storeActions.fetchStart());
    return taskService.getTaskEdit(id)
        .then(
            edit => {
                dispatch(storeActions.taskEditSuccess(edit));
            },
            error => {
                handleApiError(error, dispatch);
                dispatch(storeActions.taskEditFailure());
                customHistory.push("/task");
            })
        .finally(() => dispatch(storeActions.fetchEnd()));
};

//Document
export type DocumentThunkDispatch = ThunkDispatch<storeTypes.IApplicationState, null, storeTypes.DocumentAction>

export const getDocumentList = (folderId: string, companyId: string, page: number, filter: string, sortColumn: string, sortOrderIsAsc: boolean): ThunkAction<any, storeTypes.IApplicationState, any, any> => (dispatch, getState) => {
    dispatch(storeActions.documentListRequest());
    dispatch(storeActions.fetchStart());
    return documentService.getDocumentList(folderId, companyId, page, filter, sortColumn, sortOrderIsAsc)
        .then(
            list => {
                dispatch(storeActions.documentListSuccess(list, filter));
            },
            error => {
                handleApiError(error, dispatch);
                dispatch(storeActions.documentListFailure());
            })
        .finally(() => dispatch(storeActions.fetchEnd()));
};

export const searchDocuments = (folderId: string, companyId: string, page: number, filter: string, sortColumn: string, sortOrderIsAsc: boolean): ThunkAction<any, storeTypes.IApplicationState, any, any> => (dispatch, getState) => {
    dispatch(storeActions.documentListRequest());
    dispatch(storeActions.fetchStart());
    return documentService.searchDocuments(folderId, companyId, page, filter, sortColumn, sortOrderIsAsc)
        .then(
            list => {
                dispatch(storeActions.documentListSuccess(list, filter));
            },
            error => {
                handleApiError(error, dispatch);
                dispatch(storeActions.documentListFailure());
            })
        .finally(() => dispatch(storeActions.fetchEnd()));
};

export const addFolderToFavorites = (folderId: string, companyId: string): ThunkAction<any, storeTypes.IApplicationState, any, any> => (dispatch, getState) => {
    dispatch(storeActions.fetchStart());
    return documentService.addFolderToFavorites(folderId, companyId)
        .then(
            result => {
                dispatch(storeActions.documentListAddFavorite(folderId));
            },
            error => {
                handleApiError(error, dispatch);
                dispatch(storeActions.documentListFailure());
            })
        .finally(() => dispatch(storeActions.fetchEnd()));
};

export const removeFolderFromFavorites = (folderId: string, companyId: string): ThunkAction<any, storeTypes.IApplicationState, any, any> => (dispatch, getState) => {
    dispatch(storeActions.fetchStart());
    return documentService.removeFolderFromFavorites(folderId, companyId)
        .then(
            result => {
                dispatch(storeActions.documentListRemoveFavorite(folderId));
            },
            error => {
                handleApiError(error, dispatch);
                dispatch(storeActions.documentListFailure());
            })
        .finally(() => dispatch(storeActions.fetchEnd()));
};

export const getDocumentView = (documentVersionId: string, companyId: string): ThunkAction<any, storeTypes.IApplicationState, any, any> => (dispatch, getState) => {
    dispatch(storeActions.fetchStart());
    return documentService.getDocumentView(documentVersionId, companyId)
        .then(
            documentView => {
                dispatch(storeActions.documentViewSuccess(documentView));
            },
            error => {
                handleApiError(error, dispatch);
                dispatch(storeActions.documentViewFailure());
                const returnUrl = getState().document?.documentView?.returnUrl;
                customHistory.push(returnUrl || "/document");
            })
        .finally(() => dispatch(storeActions.fetchEnd()));
};

//UserFurtherEducation
export type UserFurtherEducationThunkDispatch = ThunkDispatch<storeTypes.IApplicationState, null, storeTypes.UserFurtherEducationAction>

export const getUserFurtherEducationList = (page: number, archived: boolean): ThunkAction<any, storeTypes.IApplicationState, any, any> => (dispatch, getState) => {
    dispatch(storeActions.userFurtherEducationListRequest());
    dispatch(storeActions.fetchStart());
    return userFurtherEducationService.getUserFurtherEducationList(page, archived)
        .then(
            list => {
                dispatch(storeActions.userFurtherEducationListSuccess(list, archived));
            },
            error => {
                handleApiError(error, dispatch);
                dispatch(storeActions.userFurtherEducationListFailure());
            })
        .finally(() => dispatch(storeActions.fetchEnd()));
};

export const userFurtherEducationListRefresh = (page: number, archived: boolean): ThunkAction<any, storeTypes.IApplicationState, any, any> => (dispatch, getState) => {
    dispatch(storeActions.taskListRequest());
    dispatch(storeActions.fetchStart());
    return userFurtherEducationService.userFurtherEducationListRefresh(page, archived)
        .then(
            list => {
                dispatch(storeActions.userFurtherEducationListRefresh(list, archived));
            },
            error => {
                handleApiError(error, dispatch);
                dispatch(storeActions.userFurtherEducationListFailure());
            })
        .finally(() => dispatch(storeActions.fetchEnd()));
};

export const getUserFurtherEducationEdit = (id: string): ThunkAction<any, storeTypes.IApplicationState, any, any> => (dispatch, getState) => {
    dispatch(storeActions.userFurtherEducationEditRequest());
    dispatch(storeActions.fetchStart());
    return userFurtherEducationService.getUserFurtherEducationEdit(id)
        .then(
            edit => {
                dispatch(storeActions.userFurtherEducationEditSuccess(edit));
            },
            error => {
                handleApiError(error, dispatch);
                dispatch(storeActions.userFurtherEducationEditFailure());
                const returnUrl = getState().userFurtherEducation?.userFurtherEducationEdit?.returnUrl;
                customHistory.push(returnUrl || "/userfurthereducation");
            })
        .finally(() => dispatch(storeActions.fetchEnd()));
};

//InstantMessage
export type InstantMessageThunkDispatch = ThunkDispatch<storeTypes.IApplicationState, null, storeTypes.InstantMessageAction>

export const getInstantMessageChannelList = (): ThunkAction<any, storeTypes.IApplicationState, any, any> => (dispatch, getState) => {
    dispatch(storeActions.instantMessageChannelListRequest());
    dispatch(storeActions.fetchStart());
    return instantMessageChannelService.getInstantMessageChannelItems()
        .then(
            list => {
                dispatch(storeActions.instantMessageChannelListSuccess(list));
            },
            error => {
                handleApiError(error, dispatch);
                dispatch(storeActions.instantMessageChannelListFailure());
            })
        .finally(() => dispatch(storeActions.fetchEnd()));
};

export const getInstantMessageChannelEdit = (id: string): ThunkAction<any, storeTypes.IApplicationState, any, any> => (dispatch, getState) => {
    dispatch(storeActions.instantMessageChannelEditRequest());
    dispatch(storeActions.fetchStart());
    return instantMessageChannelService.getInstantMessageChannelEdit(id)
        .then(
            item => {
                dispatch(storeActions.instantMessageChannelEditSuccess(item));
            },
            error => {
                handleApiError(error, dispatch);
                dispatch(storeActions.instantMessageChannelEditFailure());
                const returnUrl = getState().instantMessage?.instantMessageChannelEdit?.returnUrl;
                customHistory.push(returnUrl || "/instantmessage");
            })
        .finally(() => dispatch(storeActions.fetchEnd()));
};

//CalendarTask
export type CalendarTaskThunkDispatch = ThunkDispatch<storeTypes.IApplicationState, null, storeTypes.CalendarTaskAction>

export const getCalendarTaskView = (startDate: number, endDate: number, filter: string, stateId: string, categoryId: string): ThunkAction<any, storeTypes.IApplicationState, any, any> => (dispatch, getState) => {
    dispatch(storeActions.calendarTaskViewRequest());
    dispatch(storeActions.fetchStart());

    return calendarTaskService.getMobileCalendarTaskItems(!startDate ? null : startDate, !endDate ? null : endDate, filter, stateId !== "-1" ? [stateId.toInteger()] : null, categoryId !== Base.emptyGuid ? [categoryId] : null)
        .then(
            items => {
                dispatch(storeActions.calendarTaskViewSuccess(items, filter, stateId, categoryId));
            },
            error => {
                handleApiError(error, dispatch);
                dispatch(storeActions.calendarTaskViewFailure());
            })
        .finally(() => dispatch(storeActions.fetchEnd()));
};

export const getCalendarTaskEdit = (calendarTaskId: string, databaseRecordType: DatabaseRecordType): ThunkAction<any, storeTypes.IApplicationState, any, any> => (dispatch, getState) => {
    dispatch(storeActions.calendarTaskEditRequest());
    dispatch(storeActions.fetchStart());
    return calendarTaskService.getCalendarTaskEdit(calendarTaskId, null, databaseRecordType)
        .then(
            item => {
                dispatch(storeActions.calendarTaskEditSuccess(item));
            },
            error => {
                handleApiError(error, dispatch);
                dispatch(storeActions.calendarTaskEditFailure());
                const returnUrl = getState().calendarTask?.calendarTaskEdit?.returnUrl;
                customHistory.push(returnUrl || "/calendartask");
            })
        .finally(() => dispatch(storeActions.fetchEnd()));
};

//IdCardView
export type IdCardThunkDispatch = ThunkDispatch<storeTypes.IApplicationState, null, storeTypes.IdCardViewAction>

export const getUserIdCard = (): ThunkAction<any, storeTypes.IApplicationState, any, any> => (dispatch, getState) => {
    dispatch(storeActions.idCardViewRequest());
    dispatch(storeActions.fetchStart());
    return recordUserService.getUserIdCardItem()
        .then(
            item => {
                dispatch(storeActions.idCardViewSuccess(item));
            },
            error => {
                handleApiError(error, dispatch);
                dispatch(storeActions.idCardViewFailure());
            })
        .finally(() => dispatch(storeActions.fetchEnd()));
};